import { Check, ChevronDown, type LucideIcon } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * Device picker shared by the pre-join screen and the in-call control bar, so
 * switching a headset mid-interview looks the same as choosing one before it.
 */
export function DeviceMenu({
  icon: Icon,
  label,
  devices,
  activeDeviceId,
  onSelect,
  align = 'start',
  side = 'bottom',
  className,
  disabled,
}: {
  icon: LucideIcon;
  label: string;
  devices: MediaDeviceInfo[];
  activeDeviceId: string;
  onSelect: (deviceId: string) => void;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  className?: string;
  disabled?: boolean;
}) {
  const active = devices.find((d) => d.deviceId === activeDeviceId) ?? devices[0];
  const activeLabel = active?.label || `Default ${label.toLowerCase()}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled || devices.length === 0}
        className={cn(
          'border-border/70 group flex min-h-11 w-full items-center gap-2.5 rounded-lg border bg-white/[0.03] px-3 text-left text-sm transition-colors',
          'hover:bg-white/[0.06] disabled:pointer-events-none disabled:opacity-50',
          'focus-visible:outline-ring/70 focus-visible:outline-2 focus-visible:outline-offset-2',
          className,
        )}
      >
        <Icon className="text-muted-foreground size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{activeLabel}</span>
        <ChevronDown className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align={align} side={side} className="max-w-[min(20rem,90vw)]">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {devices.map((device, index) => {
          const selected = device.deviceId === active?.deviceId;
          return (
            <DropdownMenuItem key={device.deviceId} onSelect={() => onSelect(device.deviceId)}>
              <Check className={cn('size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
              <span className="truncate">{device.label || `${label} ${index + 1}`}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
